import styled from 'styled-components';
import {media} from '../../styles/components/media-queries';

export const CodeCard = styled.div`
  background-color: ${p => p.theme.backgroundColor};
  padding: 3rem;
  margin-bottom: 3rem;
  border-bottom: solid 0.1rem ${p => p.theme.borderColor};
  ${media.smaller`
    padding: 2rem 1rem 0;
  `};
`;

export const CodeDisplay = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 18rem;
  background: linear-gradient(
    135deg,
    ${p => p.theme.secondaryBgColor} 0%,
    ${p => p.theme.backgroundColor} 100%
  );
  border-radius: 1.2rem;
  margin: 2rem 0;
  ${media.smaller`
    min-height: 15rem;
  `};
`;

export const SubTitle = styled.h2`
  font-size: 2.4rem;
  color: ${p => p.theme.blackColor};
  text-align: center;
  margin: 0 0 1rem 0;
  font-weight: bold;
  ${media.medium`
    font-size: 2rem;
  `};
  ${media.smaller`
    font-size: 1.8rem;
  `};
`;

export const InfoText = styled.p`
  font-size: 1.6rem;
  color: ${p => p.theme.textColor};
  text-align: center;
  margin: 0 0 2rem 0;
  line-height: 2.4rem;
  ${media.smaller`
    font-size: 1.4rem;
  `};
`;

export const CodeNumber = styled.div`
  display: flex;
  font-family: sans-serif;
  gap: 0;
  justify-content: center;
  align-items: center;
  padding: 2rem 2.4rem;
  flex-wrap: wrap;
  font-weight: 600;
  border-radius: 0.8rem;
  border: 0.2rem solid ${p => p.theme.primaryColor};

  span {
    width: 6rem;
    height: 7rem;
    color: ${p => p.theme.primaryColor};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 6rem;

    &:hover {
      transform: scale(1.05);
    }

    ${media.medium`
      width: 5rem;
      height: 6rem;
      font-size: 5rem;
    `};

    ${media.smaller`
      width: 4.5rem;
      height: 6rem;
      font-size: 5rem;
    `};
  }
`;

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem 0;
`;

export const ErrorMessage = styled.div`
  font-size: 1.6rem;
  color: ${p => p.theme.errorColor};
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 2.4rem;
`;

export const RefreshButton = styled.button`
  background-color: ${p => p.theme.primaryColor};
  color: ${p => p.theme.primaryTextColor};
  border: none;
  border-radius: 0.8rem;
  padding: 1.2rem 2.4rem;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${p => p.theme.secondaryColor};
    transform: translateY(-0.2rem);
    box-shadow: 0 0.4rem 0.8rem rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  ${media.smaller`
    padding: 1rem 2rem;
    font-size: 1.4rem;
  `};
`;

export const IconWrapper = styled.span`
  font-size: 2rem;
  line-height: 1;
  display: inline-flex;
  animation: rotate 1s ease-in-out;

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

export const InstructionsSection = styled.div`
  padding: 3rem;
  background-color: ${p => p.theme.backgroundColor};
  ${media.smaller`
    padding: 0 1rem;
  `};
`;

export const InstructionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  padding: 2rem 0;
  border-bottom: 0.1rem solid ${p => p.theme.secondaryBgColor};

  &:last-child {
    border-bottom: none;
  }

  strong {
    font-size: 1.8rem;
    color: ${p => p.theme.secondaryColor};
    display: block;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 1.5rem;
    color: ${p => p.theme.textColor};
    line-height: 2.2rem;
    margin: 0;
  }

  ${media.smaller`
    gap: 1.5rem;
    padding: 1.5rem 0;

    strong {
      font-size: 1.6rem;
    }

    p {
      font-size: 1.4rem;
    }
  `};
`;

export const InstructionIcon = styled.div`
  width: 4rem;
  height: 4rem;
  min-width: 4rem;
  background-color: ${p => p.theme.primaryColor};
  color: ${p => p.theme.primaryTextColor};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  flex-shrink: 0;
  ${media.smaller`
    width: 3.5rem;
    height: 3.5rem;
    min-width: 3.5rem;
    font-size: 1.8rem;
  `};
`;

export const TimerDisplay = styled.div`
  font-size: 1.6rem;
  color: ${p => (p.$warning ? p.theme.errorColor : p.theme.textColor)};
  font-weight: 600;
  text-align: center;
  padding: 1rem 2rem;
  background-color: ${p => p.theme.secondaryBgColor};
  border-radius: 0.8rem;
  display: inline-block;

  ${media.smaller`
    font-size: 1.4rem;
    padding: 0.8rem 1.6rem;
  `};
`;

export const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
  width: 100%;
  margin-top: 2rem;

  ${media.smaller`
    gap: 1rem;
  `};
`;

export const ExpiredMessage = styled.div`
  font-size: 1.6rem;
  color: ${p => p.theme.tertiaryColor};
  text-align: center;
  margin: 1.5rem 0;
  padding: 1.5rem 2rem;
  background-color: ${p => p.theme.secondaryBgColor};
  border-radius: 0.8rem;
  text-transform: uppercase;

  ${media.smaller`
    font-size: 1.6rem;
    padding: 1.2rem 1.6rem;
  `};
`;
